/**
 * MobilePopover — Radix popover wrapped to Contract §5.
 *
 *   • flip + shift collision modifiers (never overflows viewport)
 *   • max-width capped to 92vw, max-height capped to dvh-safe
 *   • native click-outside + ESC dismiss (Radix built-in)
 *   • focus restoration to trigger on close
 *   • z-tier from Z_TIERS.POPOVER (Contract §9)
 *
 * Two presentation modes:
 *   ── Structured (default) ──
 *     The primitive renders the dark-themed scrolling popover panel.
 *     Pass children — they sit inside the styled shell.
 *
 *       <MobilePopover>
 *         <MobilePopover.Trigger asChild><button /></MobilePopover.Trigger>
 *         <MobilePopover.Content testid="info-popover">{body}</MobilePopover.Content>
 *       </MobilePopover>
 *
 *   ── Bare (bareContent=true) ──
 *     Caller owns the entire panel JSX (colours, padding, header,
 *     footer). The primitive only enforces the wrapper invariants:
 *     collision detection, max-width, max-height (safe-area aware),
 *     internal scroll, z-tier, animations. Migration target for
 *     hand-rolled `absolute right-0 …` notification panels.
 *
 *       <MobilePopover>
 *         <MobilePopover.Trigger asChild>{trigger}</MobilePopover.Trigger>
 *         <MobilePopover.Content bareContent testid="bell-popover">
 *           <div className="bg-white rounded-2xl …">…</div>
 *         </MobilePopover.Content>
 *       </MobilePopover>
 *
 * Phase 2 Batch 5 (May 14, 2026) — added bareContent mode.
 */
import * as Popover from "@radix-ui/react-popover";
import { Z_CLASS } from "./zTiers";

export function MobilePopover(props) {
  return <Popover.Root {...props} />;
}

MobilePopover.Trigger = function MobilePopoverTrigger(props) {
  return <Popover.Trigger {...props} />;
};

MobilePopover.Anchor = function MobilePopoverAnchor(props) {
  return <Popover.Anchor {...props} />;
};

MobilePopover.Close = function MobilePopoverClose(props) {
  return <Popover.Close {...props} />;
};

MobilePopover.Content = function MobilePopoverContent({
  children,
  testid = "mobile-popover",
  side = "bottom",
  align = "center",
  sideOffset = 8,
  collisionPadding = 12,
  className = "",
  bareContent = false,
  ...rest
}) {
  // Bare mode: caller owns the visual panel. We still apply the
  // Radix collision modifiers + Z_CLASS.POPOVER + max-w-[92vw] +
  // max-h-screen-safe so the panel can never overflow the viewport.
  const wrapperClass = bareContent
    ? [
        Z_CLASS.POPOVER,
        "max-w-[92vw]",
        "max-h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-4rem)]",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
        className,
      ].filter(Boolean).join(" ")
    : [
        Z_CLASS.POPOVER,
        "w-[min(92vw,360px)]",
        "max-h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-4rem)]",
        "overflow-y-auto overscroll-contain",
        "rounded-xl border border-white/10 bg-[#0a0a0a] text-white shadow-2xl p-4",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
        className,
      ].filter(Boolean).join(" ");

  return (
    <Popover.Portal>
      <Popover.Content
        data-testid={testid}
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        avoidCollisions
        className={wrapperClass}
        {...rest}
      >
        {children}
      </Popover.Content>
    </Popover.Portal>
  );
};

export default MobilePopover;
